/**
 * THE FLOW LANE's geometry (2026-09-13): the server's flow view (every coupling with its
 * orientation and the potential at each end, every element's roles) becomes segments the
 * canvas can draw -- one per edge whose two elements are on the canvas -- with the plan
 * centroid of each element as the endpoint. Pure, so the arithmetic is testable without a
 * canvas: the projection to pixels stays the canvas's.
 */
import type { components } from "../../api";
import type { Drawable } from "./queries";
import { isWhole, textAttribute } from "./queries";

export type FlowEdgeView = components["schemas"]["FlowEdgeView"];
export type FlowView = components["schemas"]["FlowView"];
export type FlowRole = components["schemas"]["Role"];

/** A point in model space, metres. */
export interface Centroid {
  x: number;
  y: number;
  z: number;
}

/** One coupling as a drawable segment: its ends in model space, and what the edge is. */
export interface FlowSegment {
  from: Centroid;
  to: Centroid;
  edge: FlowEdgeView;
  /** The mean potential along the edge, or null where the warming did not solve it. */
  phi: number | null;
}

/** The plan centroid of an element's GeoJSON coordinates, whatever the nesting; null with none. */
export function centroidOf(element: Drawable): Centroid | null {
  const geom = element.geometry as { coordinates?: unknown } | null | undefined;
  if (!geom?.coordinates) return null;
  let sx = 0;
  let sy = 0;
  let sz = 0;
  let n = 0;
  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === "number") {
      sx += node[0] as number;
      sy += (node[1] as number) ?? 0;
      sz += (node[2] as number) ?? 0;
      n += 1;
      return;
    }
    for (const child of node) walk(child);
  };
  walk(geom.coordinates);
  if (n === 0) return null;
  return { x: sx / n, y: sy / n, z: sz / n };
}

/** The segments for every edge whose two ends are among the elements handed in. */
export function flowSegments(
  edges: FlowEdgeView[],
  elements: Drawable[],
): FlowSegment[] {
  const at = new Map<string, Centroid>();
  for (const element of elements) {
    const c = centroidOf(element);
    if (c) at.set(element.origin_id, c);
  }
  const segments: FlowSegment[] = [];
  for (const edge of edges) {
    const from = at.get(edge.a);
    const to = at.get(edge.b);
    if (!from || !to) continue;
    const phi =
      edge.phi_a != null && edge.phi_b != null
        ? (edge.phi_a + edge.phi_b) / 2
        : null;
    segments.push({ from, to, edge, phi });
  }
  return segments;
}

/** What the hover says of an edge: the ends, how it was oriented, the potentials. */
export function flowText(edge: FlowEdgeView): string {
  const arrow = edge.orientation === "unoriented" ? "—" : "→";
  const phi =
    edge.phi_a != null && edge.phi_b != null
      ? ` · φ ${edge.phi_a.toFixed(2)} ${arrow} ${edge.phi_b.toFixed(2)}`
      : "";
  return `${edge.a} ${arrow} ${edge.b} · ${edge.orientation}${phi}`;
}

/** What the hover says of an element: its name, and with the flow on, its roles and its potential. */
export function elementText(
  element: Drawable,
  flow: FlowView | null | undefined,
): string {
  // A whole element says its authored name, else its class; a thin row says its class.
  const name =
    textAttribute(element, "element_name") ??
    textAttribute(element, "ifc_class") ??
    (isWhole(element)
      ? element.origin_id
      : element.ifc_class || element.origin_id);
  if (!flow) return name;
  const roles = flow.roles?.[element.origin_id] ?? [];
  const edge = flow.edges.find(
    (e) => e.a === element.origin_id || e.b === element.origin_id,
  );
  const phi = edge
    ? edge.a === element.origin_id
      ? edge.phi_a
      : edge.phi_b
    : null;
  const parts = [name];
  if (roles.length > 0) parts.push(roles.join("+"));
  if (phi != null) parts.push(`φ ${phi.toFixed(2)}`);
  return parts.join(" · ");
}
